import { useId } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/shadcn/button';
import LoadingTimeline from '@/components/UI/loading/LoadingTimeline';
import { getElapsedTime } from '@/utils';
import type { ProjectAudit } from '../../api/types';

interface ProjectAuditUIProps {
  audits: ProjectAudit[];
  page: number;
  pageSize: number;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onPrevious: () => void;
  onNext: () => void;
}

const formatDescription = (description?: string) => {
  const auditDescription = description ?? '';
  let formattedDescription = auditDescription.split(':')[1]?.trim() || auditDescription;

  if (auditDescription.includes('created')) {
    formattedDescription = 'Created project';
  }

  if (formattedDescription.includes('Task') || formattedDescription.includes('Deliverable') || formattedDescription.includes('Milestone')) {
    const descriptionParts = formattedDescription.split(' ');
    const tdmType = descriptionParts[0];
    const tdmId = descriptionParts[2];

    if (!tdmType || !tdmId) return formattedDescription;

    return (
      <>
        updated {tdmType}{' '}
        <Link to={`/terminal/dashboard/${tdmType}s/${tdmId}`} className='cursor-pointer text-blue-500 underline'>
          {tdmId}
        </Link>
      </>
    );
  }

  if (formattedDescription.includes('Added') && formattedDescription.includes('asset')) {
    formattedDescription = `uploaded file - ${formattedDescription.split('-')[1]}`;
  }

  return formattedDescription;
};

export function ProjectAuditUI({ audits, page, pageSize, isLoading, isError, onRetry, onPrevious, onNext }: ProjectAuditUIProps) {
  const isEmpty = !isLoading && !isError && audits.length === 0;
  const timelineTitleId = useId();

  return (
    <section aria-labelledby={timelineTitleId} className='divide-y bg-white shadow sm:rounded-lg'>
      <div className='py-5'>
        <h2
          id={timelineTitleId}
          className='m-0 border-l-4 border-primaryColor-normal px-6 py-2 text-lg font-medium text-primaryColor-normal text-gray-900'
        >
          Project History
        </h2>
      </div>
      <div className='flow-root px-6 py-6'>
        {isLoading && <LoadingTimeline />}
        {isError && (
          <div role='alert' className='space-y-3 text-sm text-gray-600'>
            <p>Unable to load project history. Please try again.</p>
            <Button type='button' variant='outline' onClick={onRetry}>
              Retry
            </Button>
          </div>
        )}
        {isEmpty && <p className='text-sm text-gray-500'>No Project History</p>}
        {!isLoading && !isError && audits.length > 0 && (
          <dl className='space-y-5'>
            {audits.map((audit) => (
              <div key={audit.id} className='relative pb-5'>
                <span className='absolute top-4 left-1.5 -ml-px h-full w-0.5 bg-gray-200' aria-hidden='true' />
                <div className='flex min-w-0 space-x-4'>
                  <span className='mt-1 flex h-3 w-3 items-center justify-center rounded-full bg-tertiaryColor-light' />
                  <div className='flex flex-col whitespace-wrap text-sm text-gray-500'>
                    <p className='break-words'>
                      <strong>{audit.user?.name || 'System'}</strong>{' '}
                      <span className='lowercase'>{formatDescription(audit.description)}</span>
                    </p>
                    <time dateTime={audit.createdAt} className='text-xs'>
                      {getElapsedTime(new Date(audit.createdAt).getTime() / 1000)}
                    </time>
                  </div>
                </div>
              </div>
            ))}
          </dl>
        )}
      </div>
      <div className='flex justify-end gap-4 p-4'>
        <Button type='button' variant='outline' onClick={onPrevious} disabled={page === 1}>
          Previous
        </Button>
        <Button type='button' onClick={onNext} disabled={isLoading || isError || audits.length < pageSize}>
          Next
        </Button>
      </div>
    </section>
  );
}
